import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateBlogMutation } from '../../store/slices/apiSlice';
import { ArrowLeft, Save, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateSlug } from '../../utils/slugUtils';

const AddBlog: React.FC = () => {
  const navigate = useNavigate();
  const [createBlog, { isLoading: isCreating }] = useCreateBlogMutation();
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    desc: '',
    content: '',
    category: '',
    author: '',
    tags: '',
    seoKeywords: '',
    readTime: '',
    status: 'published',
    featured: false,
    metaTitle: '',
    metaDescription: '',
    publishedBy: 'Admin',
    file: null as File | null
  });
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);

  // Auto-generate slug from title when title changes (only if slug wasn't manually edited)
  useEffect(() => {
    if (!isSlugManuallyEdited && formData.title) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(formData.title)
      }));
    }
  }, [formData.title, isSlugManuallyEdited]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const formDataToSend = new FormData();
      
      // Add all form fields
      formDataToSend.append('title', formData.title);
      formDataToSend.append('slug', formData.slug || generateSlug(formData.title));
      formDataToSend.append('desc', formData.desc);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('author', formData.author);
      
      // Handle tags and seoKeywords as arrays
      if (formData.tags) {
        const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        tagsArray.forEach(tag => {
          formDataToSend.append('tags[]', tag);
        });
      }
      
      if (formData.seoKeywords) {
        const keywordsArray = formData.seoKeywords.split(',').map(keyword => keyword.trim()).filter(keyword => keyword);
        keywordsArray.forEach(keyword => {
          formDataToSend.append('seoKeywords[]', keyword);
        });
      }
      
      formDataToSend.append('readTime', formData.readTime);
      formDataToSend.append('status', formData.status);
      formDataToSend.append('featured', formData.featured.toString());
      formDataToSend.append('metaTitle', formData.metaTitle);
      formDataToSend.append('metaDescription', formData.metaDescription);
      formDataToSend.append('publishedBy', formData.publishedBy);
      
      // Add file if selected
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }
      
      const result = await createBlog(formDataToSend).unwrap();
      
      if (result.success) {
        toast.success('Blog created successfully!');
        navigate('/admin/blogs');
      } else {
        toast.error(result.message || 'Failed to create blog');
      }
    } catch (error: any) {
      console.error('Error creating blog:', error);
      
      if (error.data) {
        toast.error(error.data.message || 'Failed to create blog');
      } else if (error.status) {
        toast.error(`Server error: ${error.status}`);
      } else {
        toast.error('Failed to create blog');
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/blogs')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Add New Blog</h1>
            <p className="text-gray-600 mt-1">Create a new blog post</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Title and Author */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                placeholder="Enter blog title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Author <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.author}
                onChange={(e) => setFormData({...formData, author: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                placeholder="Enter author name"
              />
            </div>
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Slug <span className="text-red-500">*</span>
              <span className="text-xs text-gray-500 ml-2">(Auto-generated from title, but you can edit it)</span>
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => {
                setFormData({...formData, slug: e.target.value});
                setIsSlugManuallyEdited(true);
              }}
              onBlur={(e) => {
                // Clean up slug on blur if empty
                if (!e.target.value.trim()) {
                  setFormData(prev => ({
                    ...prev,
                    slug: generateSlug(prev.title)
                  }));
                  setIsSlugManuallyEdited(false);
                } else {
                  // Ensure slug is properly formatted
                  setFormData(prev => ({
                    ...prev,
                    slug: generateSlug(e.target.value)
                  }));
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="blog-post-slug"
            />
            <p className="text-xs text-gray-500 mt-1">
              URL-friendly version of the title. Will be auto-generated from title, but you can customize it.
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.desc}
              onChange={(e) => setFormData({...formData, desc: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              required
              placeholder="Enter a brief description"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={10}
              required
              placeholder="Enter blog content (HTML supported)"
            />
          </div>

          {/* Category and Read Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Category</option>
                <option value="Laundry care">Laundry care</option>
                <option value="Investment">Investment</option>
                <option value="Real estate">Real estate</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Read Time (minutes) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.readTime}
                onChange={(e) => setFormData({...formData, readTime: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                placeholder="e.g., 5"
              />
            </div>
          </div>

          {/* Tags and SEO Keywords */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags (comma separated)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="tag1, tag2, tag3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SEO Keywords (comma separated)
              </label>
              <input
                type="text"
                value={formData.seoKeywords}
                onChange={(e) => setFormData({...formData, seoKeywords: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>
          </div>

          {/* Status and Featured */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            <div className="flex items-center pt-8">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="featured" className="ml-2 text-sm font-medium text-gray-700">
                Featured Blog
              </label>
            </div>
          </div>

          {/* Meta Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meta Title
            </label>
            <input
              type="text"
              value={formData.metaTitle}
              onChange={(e) => setFormData({...formData, metaTitle: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="SEO meta title"
            />
          </div>

          {/* Meta Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meta Description
            </label>
            <textarea
              value={formData.metaDescription}
              onChange={(e) => setFormData({...formData, metaDescription: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="SEO meta description"
            />
          </div>

          {/* Featured Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Image
            </label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <Upload className="w-5 h-5 text-gray-600 mr-2" />
                <span className="text-sm text-gray-600">Choose File</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({...formData, file: e.target.files?.[0] || null})}
                  className="hidden"
                />
              </label>
              {formData.file && (
                <span className="text-sm text-gray-600">{formData.file.name}</span>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/admin/blogs')}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isCreating}
            className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Create Blog
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBlog;





